import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_69566509-b9c0-48ae-b149-6b9accd67eee/artifacts/3nmi4pqp_Dynamic%20Logo%20with%20Sapphire%20Coral%20Gradient%20%288%29.png";

const Terms = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      {/* Header */}
      <header className="nav">
        <div className="nav-content">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Shadesense.ai" className="h-10 w-auto" />
          </Link>
          <Link to="/" className="btn-secondary flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-meta uppercase tracking-widest mb-4" style={{ color: 'var(--accent-primary)' }}>
            Legal
          </p>
          <h1 className="text-display mb-4" style={{ color: 'var(--text-primary)' }}>
            Terms of Use
          </h1>
          <p className="text-body text-secondary mb-12">
            Effective Date: January 1, 2026
          </p>

          <div className="prose prose-lg" style={{ color: 'var(--text-secondary)' }}>
            <p className="text-body mb-6">
              These Terms of Use ("Terms") govern your access to and use of the Shadesense.ai website and web application (the "Service"), operated by Shadesense Inc., an Illinois corporation ("Shadesense," "we," "our," or "us").
            </p>
            <p className="text-body mb-8">
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>1. Eligibility</h2>
            <p className="text-body mb-6">
              The Service is intended for users 13 years of age or older. By using the Service, you represent that you meet this requirement. If you are under 18, you may use the Service only with the consent of a parent or legal guardian.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>2. Accounts</h2>
            <p className="text-body mb-4">
              Certain features of the Service may be accessed without creating an account. Creating an account is optional and may enhance your experience.
            </p>
            <p className="text-body mb-4">If you create an account, you agree to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li className="text-body">Provide accurate and complete information</li>
              <li className="text-body">Maintain the confidentiality of your login credentials</li>
              <li className="text-body">Accept responsibility for all activity under your account</li>
            </ul>
            <p className="text-body mb-6">
              You may delete your account at any time through your account settings. We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>3. Use of the Service</h2>
            <p className="text-body mb-4">
              You may use the Service for lawful, personal, and non-commercial purposes only.
            </p>
            <p className="text-body mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li className="text-body">Upload content you do not own or have the right to use</li>
              <li className="text-body">Use the Service for unlawful, harmful, or abusive purposes</li>
              <li className="text-body">Attempt to interfere with the Service's functionality or security</li>
              <li className="text-body">Reverse-engineer, copy, scrape, or exploit any part of the Service without authorization</li>
            </ul>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>4. User Content</h2>
            <p className="text-body mb-4">
              You retain full ownership of any images or content you upload ("User Content").
            </p>
            <p className="text-body mb-4">
              By uploading User Content, you grant Shadesense a limited, non-exclusive, royalty-free license to process that content solely to provide the Service.
            </p>
            <p className="text-body mb-4">Uploaded images are:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li className="text-body">Processed automatically</li>
              <li className="text-body">Stored temporarily</li>
              <li className="text-body">Not used for model training</li>
              <li className="text-body">Not reviewed by humans</li>
            </ul>
            <p className="text-body mb-6">
              Shadesense does not claim ownership of User Content.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>5. Intellectual Property</h2>
            <p className="text-body mb-4">
              All trademarks, logos, designs, software, copy, algorithms, and visual elements of the Service (excluding User Content) are the property of Shadesense Inc. or its licensors and are protected by intellectual property laws.
            </p>
            <p className="text-body mb-6">
              You may not copy, reproduce, modify, distribute, or exploit any portion of the Service without prior written permission.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>6. Product Recommendations & Affiliate Links</h2>
            <p className="text-body mb-4">
              Shadesense.ai provides product recommendations for informational and discovery purposes only.
            </p>
            <p className="text-body mb-4">
              Some product listings and recommendations may include affiliate links to third-party retailers. If you choose to click on a product link and make a purchase, Shadesense may earn a commission at no additional cost to you.
            </p>
            <p className="text-body mb-4">Important disclosures:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li className="text-body">Affiliate links are activated only when a user voluntarily clicks on a product or shopping link</li>
              <li className="text-body">Shadesense does not automatically redirect users to retailers</li>
              <li className="text-body">Shadesense does not trigger affiliate links, tracking pixels, or cookies without explicit user action</li>
              <li className="text-body">Affiliate relationships do not influence which products are shown or how results are ranked</li>
            </ul>
            <p className="text-body mb-6">
              Shadesense does not control third-party websites and is not responsible for their content, pricing, availability, or practices.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>7. Disclaimers & AI Limitations</h2>
            <p className="text-body mb-4">
              The Service is provided "as is" and "as available."
            </p>
            <p className="text-body mb-4">Shadesense does not guarantee:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li className="text-body">Exact color accuracy</li>
              <li className="text-body">Availability of third-party products</li>
              <li className="text-body">That results will meet personal expectations</li>
            </ul>
            <p className="text-body mb-4">
              Color analysis and recommendations are generated using automated systems and may vary due to lighting conditions, image quality, fabric texture, or device display differences.
            </p>
            <p className="text-body mb-6">
              The Service provides informational fashion insights only and does not constitute professional styling or fashion advice.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>8. Limitation of Liability</h2>
            <p className="text-body mb-6">
              To the maximum extent permitted by law, Shadesense Inc. shall not be liable for indirect, incidental, consequential, or special damages arising from your use of the Service.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>9. Arbitration</h2>
            <p className="text-body mb-6">
              Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration, governed by the laws of the State of Illinois, except where prohibited by law.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>10. Changes to These Terms</h2>
            <p className="text-body mb-6">
              We may update these Terms from time to time. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>11. Governing Law</h2>
            <p className="text-body mb-6">
              These Terms are governed by the laws of the State of Illinois, without regard to conflict-of-law principles.
            </p>

            <h2 className="text-h2 mt-12 mb-4" style={{ color: 'var(--text-primary)' }}>12. Contact</h2>
            <p className="text-body mb-6">
              For legal inquiries: <a href="mailto:aisalesfunnel@gmail.com" className="underline" style={{ color: 'var(--accent-primary)' }}>aisalesfunnel@gmail.com</a>
            </p>
          </div>

          {/* Other Legal Links */}
          <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <p className="text-small text-muted mb-4">Related Policies</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy" className="text-small hover:underline" style={{ color: 'var(--accent-primary)' }}>Privacy Policy</Link>
              <Link to="/cookies" className="text-small hover:underline" style={{ color: 'var(--accent-primary)' }}>Cookie Policy</Link>
              <Link to="/policies" className="text-small hover:underline" style={{ color: 'var(--accent-primary)' }}>All Policies</Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="text-meta text-muted">© 2026 Shadesense Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
